// ABOUTME: Keeps the Marketing lead's thread in step with platform requests: the
// All / Me / <platform> filter (kept in the URL, so it survives a reload) and the
// live arrival of new requests, hand-off cards and the agent's reports. The thread is read through a query under the
// ['domain', 'conversation_message'] key, which the event stream already
// invalidates whenever a message lands — no polling.
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useAIChatStore } from '@/stores/useAIChatStore'
import type { PlatformSource } from '@/api/agent'
import { loadHermesThread } from './hermesHistory'

const ALL = 'all'

export function usePlatformThread(conversationId: string | undefined) {
  const [params, setParams] = useSearchParams()
  const source = params.get('source') ?? ALL
  const restoreMessages = useAIChatStore((s) => s.restoreMessages)
  // The source whose transcript is on screen; null until the first load. This
  // query is the only loader of the thread, so a filter in the URL is never
  // overwritten by a slower unfiltered load.
  const applied = useRef<string | null>(null)
  // The switch lists every platform, so it must not vanish while another
  // filter's transcript is loading.
  const [knownSources, setKnownSources] = useState<PlatformSource[]>([])

  const { data } = useQuery({
    queryKey: ['domain', 'conversation_message', 'thread', source],
    queryFn: () => loadHermesThread(50, source === ALL ? undefined : source),
    enabled: !!conversationId,
  })

  useEffect(() => {
    if (!data || !conversationId) return
    const stored = useAIChatStore.getState().conversations.find((c) => c.id === conversationId)
    const known = new Set(
      stored?.messages.flatMap((m) => [m.id, m.metadata?.backend_message_id ?? m.id])
    )
    // A card (a platform request, a hand-off) or the agent's report from a run it was woken
    // for arrives with no chat turn of its own, so the thread is re-read to show it.
    const hasNewRequest = data.messages.some((m) => !known.has(m.id) && m.role === 'assistant')
    // A fresh business has no history: the seeded welcome message stays.
    const untouched = applied.current === null && source === ALL && data.messages.length === 0
    if (!untouched && (applied.current !== source || hasNewRequest)) {
      restoreMessages(conversationId, data.messages)
    }
    applied.current = source
  }, [data, source, conversationId, restoreMessages])

  const setSource = (next: string) => {
    const nextParams = new URLSearchParams(params)
    if (next === ALL) nextParams.delete('source')
    else nextParams.set('source', next)
    setParams(nextParams, { replace: true })
  }

  if (data && data.sources !== knownSources) setKnownSources(data.sources)

  // True from the tap on a filter until its transcript arrives, so the screen
  // never shows the previous filter's messages under the new filter.
  const loading = source !== ALL && !data
  return { source, setSource, sources: knownSources, loading }
}
